<template>
  <div class="address-information">
    <div class="address-group">
      <span class="title">
        {{ $t('account.invoiceAddress') }}
      </span>
      <div class="addresses">
        <p
          v-if="!hasInvoiceAddresses"
          class="no-address"
        >
          {{ $t('account.noAddress') }}
        </p>
        <AddressCard
          v-else
          v-for="(address,index) in invoiceAddresses"
          :key="'invoiceAddresses-'+index"
          :address="address"
          :editable="false"
          :tooltip="$t('account.tooltipTexts.refetchUpdate')"
          :is-new-seller-api="isNewSellerApi"
          @edit="editAddress"
        />
      </div>
      <ScButton
        v-if="createAddressEnabled"
        @click="addAddress"
        class="add-address-btn"
      >
        {{ $t('account.addAddressBtn') }}
      </ScButton>
    </div>
    <div class="address-group">
      <span class="title">
        {{ $t('account.shipmentAddress') }}
      </span>
      <div class="addresses">
        <p
          v-if="!hasShipmentAddresses"
          class="no-address"
        >
          {{ $t('account.noAddress') }}
        </p>
        <AddressCard
          v-else
          v-for="(address,index) in shipmentAddresses"
          type="shipmentAddress"
          :key="'shipmentAddresses-'+index"
          :address="address"
          :total-address="shipmentAddresses.length"
          :is-new-seller-api="isNewSellerApi"
          :editable="editable"
          @edit="editAddress"
        />
      </div>
      <ScButton
        v-if="createAddressEnabled"
        @click="addAddress"
        class="add-address-btn"
      >
        {{ $t('account.addAddressBtn') }}
      </ScButton>
    </div>
    <div class="address-group">
      <span class="title">
        {{ $t('account.returnAddress') }}
      </span>
      <div class="addresses">
        <p
          v-if="!hasReturnAddresses"
          class="no-address"
        >
          {{ $t('account.noAddress') }}
        </p>
        <AddressCard
          v-else
          v-for="(address,index) in returnAddresses"
          :key="'shipmentAddresses-'+index"
          :address="address"
          type="returnAddress"
          :editable="editable"
          :is-new-seller-api="isNewSellerApi"
          :total-address="returnAddresses.length"
          @edit="editAddress"
        />
      </div>
      <ScButton
        v-if="createAddressEnabled"
        @click="addAddress"
        class="add-address-btn"
      >
        {{ $t('account.addAddressBtn') }}
      </ScButton>
    </div>
    <AddressModal
      :show="modal.show"
      :type="modal.type"
      :data="modal.data"
      :address-type="modal.addressType"
      :total-address="modal.totalAddress"
      @close="closeModal"
      @submit="submitAddressModal"
    />
  </div>
</template>

<script>

import account from '@/services/account';
import AddressCard from '@/components/Info/Cards/AddressCard';
import AddressModal from '@/components/Info/Modals/AddressModal';
import ScButton from '@trendyol/sc-ui-kit/ScButton';
import SupplierService from '@/services/SupplierService';
import { remoteConfig } from '@/services';

const GTMEventNames = {
  returnAddress: 'İade Adresi Güncelle',
  shipmentAddress: 'Sevkiyat Adresi Güncelle',
};

export default {
  name: 'AddressInformation',
  components: { AddressCard, AddressModal, ScButton },
  props: {
    editable: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      shipmentAddresses: [],
      returnAddresses: [],
      invoiceAddresses: [],
      modal: {
        type: 'add',
        data: {},
        show: false,
      },
      createAddressEnabled: false,
    };
  },
  created () {
    this.getAddresses();
  },
  computed: {
    isNewSellerApi () {
      return remoteConfig.getConfigValueByKey('IS_NEW_SELLER_API', false);
    },
    hasShipmentAddresses () {
      return this.shipmentAddresses.length > 0;
    },
    hasReturnAddresses () {
      return this.returnAddresses.length > 0;
    },
    hasInvoiceAddresses () {
      return this.invoiceAddresses.length > 0;
    },
  },

  methods: {
    async getAddresses () {
      if (this.isNewSellerApi) {
        return this.getAddressesV2();
      }

      const addresses = await account.getAddresses({ hideErrorMessage: true });
      this.shipmentAddresses = addresses.shipment;
      this.invoiceAddresses = addresses.invoice;
      this.returnAddresses = addresses.returning;
    },
    async getAddressesV2 () {
      const addresses = await account.getAddressesV2({ hideErrorMessage: true });

      this.shipmentAddresses = addresses.delivery;
      this.invoiceAddresses = addresses.invoice;
      this.returnAddresses = addresses.returning;
    },
    editAddress ({ address, type: addressType, totalAddress }) {
      const newAddressObj = {
        ...address,
        address: address.addressLine || address.address,
        cityID: address.cityId || address.cityID,
        districtID: address.districtId || address.districtID,
      };

      this.modal = {
        type: 'edit',
        data: newAddressObj,
        addressType,
        totalAddress,
        show: true,
      };
    },
    addAddress () {
      this.modal = {
        type: 'add',
        data: {},
        show: true,
      };
    },
    closeModal () {
      this.modal.show = false;
    },
    async submitAddressModal ({ type, data, address, addressType }) {
      let formattedData = data.reduce((obj, item) => {
        obj[item.key] = item.value;
        return obj;
      }, {});
      const payload = {
        'cityId': formattedData.city.code,
        'countryId': formattedData.city.countryId,
        'districtId': formattedData.district.id,
        'postalCode': formattedData.postalCode,
        'supplierRegionId': address.supplierRegionId,
        'addressTypeId': address.supplierAddressTypeID,
        'address': formattedData.address,
        'isDefault': formattedData.isDefault,
      };
      if (type === 'edit') {
        const key = `${addressType}es`;
        const addresses = this[key] || [];
        const defaultAddressCount = addresses.reduce((result, nextItem) => {
          return nextItem.isDefault
            ? result + 1
            : result;
        }, 0);

        if (!payload.isDefault && defaultAddressCount <= 1) {
          return this.$toast.error('Adreslerinizden bir tanesi ön tanımlı adres olmak zorundadır.', {
            duration: 10000,
          });
        }

        let message = this.$t('account.asyncSuccessToast');

        if (this.isNewSellerApi) {
          await this.updateSellerAddressV2({ editedAddressData: formattedData, address });
        } else {
          message = this.$t('account.updateAddressToast');

          await account.updateAddress(payload, { params: { id: address.id } });
        }

        this.$toast.success(
          message, {
            duration: 3000,
          });

        this.$gtm.trackEvent({
          event: 'GAEvent',
          eventCategory: 'Onboarding - Hesabım',
          eventAction: GTMEventNames[addressType] || 'N/A',
          eventLabel: SupplierService.getSupplierId(),
        });
      } else if (type === 'add') {
        await account.createAddress(payload, { });
        this.$toast.success(this.$t('account.addAddressToast'), {
          duration: 3000,
        });
      }
      this.modal.show = false;

      setTimeout(() => {
        this.getAddresses();
      }, 500);
    },

    updateSellerAddressV2 ({ address, editedAddressData }) {
      const payload = {
        'id': address.id,
        'city': editedAddressData.city.name,
        'country': address.country,
        'countryCode': address.countryCode,
        'district': editedAddressData.district.name,
        'neighbourhood': address.neighbourhood,
        'addressType': address.addressType,
        'addressLine': editedAddressData.address,
        'postalCode': editedAddressData.postalCode,
        'regionId': address.regionId,
        'default': editedAddressData.isDefault,
      };

      return account.updateAddressV2(payload);
    },
  },
};
</script>
<style scoped lang="scss">
  .address-information{
    .address-group{
      &:not(:last-child){
        margin-bottom: 16px
      }
      .title{
        font-size: 14px;
        font-weight: 500;
        color: #95a1b5;
      }
      .addresses{
        margin-left: -10px;
        width: calc(100% + 20px);
        display: flex;
        flex-wrap: wrap;

        .no-address{
          margin-left: 10px;
          font-size: 14px;
          color: #6e7787;
        }
      }
      .add-address-btn{
        margin-bottom: 10px;
        width: 400px;
      }
    }
  }
</style>
